import { Button, Checkbox, message, Radio } from "antd";
import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ConfirmationModal } from "../../../components/confirmation-modal";
import { LoadingIndicator } from "../../../components/loading";
import { PageLayout } from "../../../components/page-layout";
import { useAuth } from "../../../contexts/auth";
import * as api from "../../../utils/api";
import * as S from "./styles";

const DEFAULT_ERROR_MESSAGE =
  "Erro inesperado. Por favor, tente novamente mais tarde.";

// Função utilitária para parsear answer_value
const parseAnswerValue = (answerValue) => {
  if (!answerValue) return [];

  // Se já é um array (vem direto do banco como JSONB)
  if (Array.isArray(answerValue)) {
    return answerValue;
  }

  // Se é uma string, tenta fazer parse JSON
  if (typeof answerValue === "string") {
    try {
      return JSON.parse(answerValue);
    } catch {
      // Se não for JSON válido, trata como string separada por vírgulas
      return answerValue.split(",").map((item) => item.trim());
    }
  }

  return [];
};

export const StoreQuestionsPage = () => {
  const { campaignId } = useParams();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [campaign, setCampaign] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [finished, setFinished] = useState(false);

  const handleGetCurrentActiveCampaign = useCallback(async () => {
    setLoading(true);

    try {
      const { result, error } = await api.post("/get-current-campaign", {
        storeId: user.id,
        campaignId,
      });

      if (error) {
        throw new Error(error);
      }

      const { campaign, questions, answers, finished } = result;

      setCampaign(campaign);
      setQuestions(questions);
      setAnswers(answers);
      setFinished(finished);
    } catch (err) {
      message.error(err.message || DEFAULT_ERROR_MESSAGE);
    } finally {
      setLoading(false);
    }
  }, [user.id, campaignId]);

  const handleSaveAnswer = async (answer) => {
    try {
      const formData = new FormData();

      const { files, ...restAnswer } = answer;

      formData.append("storeId", user?.id);
      formData.append("campaignId", campaign?.id);
      formData.append("questionId", restAnswer?.questionId);
      formData.append(
        "answer",
        Array.isArray(restAnswer?.answer)
          ? JSON.stringify(restAnswer.answer)
          : restAnswer?.answer
      );
      formData.append("comments", restAnswer?.comments);

      for (let i = 0; i < files.length; i++) {
        formData.append("files", files[i]);
      }

      const { error } = await api.post("/save-answer", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (error) {
        throw new Error(error);
      }

      message.success("Resposta salva com sucesso!");
      await handleGetCurrentActiveCampaign();
    } catch (error) {
      message.error(error.message || DEFAULT_ERROR_MESSAGE);
    }
  };

  const handleUpdateAnswer = async (answer) => {
    try {
      const formData = new FormData();

      const { files, ...restAnswer } = answer;

      formData.append("answerId", restAnswer.answerId);
      formData.append(
        "answer",
        Array.isArray(restAnswer.answer)
          ? JSON.stringify(restAnswer.answer)
          : restAnswer.answer
      );
      formData.append("comments", restAnswer.comments);

      if (files && files.length > 0) {
        for (let i = 0; i < files.length; i++) {
          formData.append("files", files[i]);
        }
      }

      const { error } = await api.post("/update-answer", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (error) {
        throw new Error(error);
      }

      message.success("Resposta atualizada com sucesso!");
      await handleGetCurrentActiveCampaign();
    } catch (error) {
      message.error(error.message || DEFAULT_ERROR_MESSAGE);
    }
  };

  useEffect(() => {
    handleGetCurrentActiveCampaign();
  }, [handleGetCurrentActiveCampaign]);

  if (loading) {
    return <LoadingIndicator />;
  }

  return (
    <PageLayout
      wrapperStyle={{
        paddingTop: 0,
        paddingLeft: 0,
        paddingRight: 0,
      }}
    >
      {campaign?.banner_file_key && (
        <S.BannerContainer
          desktopUrl={`https://plataforma-checkin-banner-files.s3.sa-east-1.amazonaws.com/${campaign?.banner_file_key}`}
          mobileUrl={`https://plataforma-checkin-banner-files.s3.sa-east-1.amazonaws.com/${
            campaign?.banner_mobile_file_key ?? campaign?.banner_file_key
          }`}
        />
      )}

      {!campaign ? (
        <div
          style={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <h2
            style={{
              color: "#fff",
              textAlign: "center",
            }}
          >
            NENHUMA CAMPANHA ATIVA NO MOMENTO
          </h2>
        </div>
      ) : (
        <S.StoreQuestionsContainer>
          <h1>{campaign?.title || "Título não encontrado"}</h1>

          {finished && (
            <h2
              style={{
                color: "green",
              }}
            >
              QUESTIONÁRIO JÁ RESPONDIDO
            </h2>
          )}

          {questions.map((item, index) => (
            <QuestionItem
              key={index}
              item={item}
              handleSaveAnswer={handleSaveAnswer}
              handleUpdateAnswer={handleUpdateAnswer}
              answer={answers.find((i) => i.question_id === item.id)}
              finished={finished}
            />
          ))}
        </S.StoreQuestionsContainer>
      )}
    </PageLayout>
  );
};

const QuestionItem = ({
  item,
  answer,
  handleSaveAnswer,
  handleUpdateAnswer,
  finished,
}) => {
  const [loading, setLoading] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [questionState, setQuestionState] = useState({
    answer:
      answer?.answer === "true"
        ? "true"
        : answer?.answer === "false"
        ? "false"
        : null,
    multipleAnswers: parseAnswerValue(answer?.answer_value),
    textAnswer: item.question_type === "text" ? answer?.answer || "" : "",
    comments: answer?.comments || "",
    files: answer?.files || [],
  });

  // Atualiza o questionState quando o answer muda
  useEffect(() => {
    setQuestionState({
      answer:
        answer?.answer === "true"
          ? "true"
          : answer?.answer === "false"
          ? "false"
          : null,
      multipleAnswers: parseAnswerValue(answer?.answer_value),
      textAnswer: item.question_type === "text" ? answer?.answer || "" : "",
      comments: answer?.comments || "",
      files: answer?.files || [],
    });
  }, [answer, item.question_type]);

  const handleOnChangeAnswer = (e) => {
    const value = e.target.value;
    setQuestionState({ ...questionState, answer: value, comments: "" });
  };

  const handleOnChangeTextAnswer = (e) => {
    const value = e.target.value;
    setQuestionState({ ...questionState, textAnswer: value });
  };

  const handleOnChangeMultipleAnswers = (checkedValues) => {
    setQuestionState({ ...questionState, multipleAnswers: checkedValues });
  };

  const handleOnChangeComments = (e) => {
    const commentsValue = e.target.value;
    setQuestionState({ ...questionState, comments: commentsValue });
  };

  const handleOnChangeFiles = (e) => {
    const fileList = Array.from(e.target.files);
    setQuestionState({ ...questionState, files: fileList });
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setQuestionState({
      answer:
        item.question_type === "multiple_answers"
          ? null
          : answer?.answer === "true"
          ? "true"
          : answer?.answer === "false"
          ? "false"
          : null,
      multipleAnswers: parseAnswerValue(answer?.answer_value),
      textAnswer: item.question_type === "text" ? answer?.answer || "" : "",
      comments: answer?.comments || "",
      files: answer?.files || [],
    });
  };

  const _handleSaveAnswer = async (e) => {
    e.preventDefault();

    // Validação baseada no tipo de pergunta
    if (item.question_type === "multiple_answers") {
      if (questionState.multipleAnswers.length === 0) {
        message.error("Por favor, selecione pelo menos uma opção.");
        return;
      }
    } else if (item.question_type === "text") {
      if (!questionState.textAnswer.trim()) {
        message.error("Por favor, digite uma resposta.");
        return;
      }
    } else {
      if (!questionState.answer) {
        message.error("Por favor, selecione uma opção.");
        return;
      }
    }

    try {
      setLoading(true);

      const answerData = {
        questionId: item.id,
        answer:
          item.question_type === "multiple_answers"
            ? questionState.multipleAnswers
            : item.question_type === "text"
            ? questionState.textAnswer
            : questionState.answer,
        comments:
          questionState.answer === "false" ? questionState.comments : null,
        files: questionState.answer === "true" ? questionState.files : [],
      };

      await handleSaveAnswer(answerData);

      setOpenConfirm(false);
    } catch (error) {
      console.error("Erro ao salvar resposta:", error);
      message.error("Erro ao salvar resposta. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const _handleUpdateAnswer = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);

      const payload = {
        answerId: answer.id,
        answer:
          item.question_type === "multiple_answers"
            ? questionState.multipleAnswers
            : item.question_type === "text"
            ? questionState.textAnswer
            : questionState.answer,
        comments:
          questionState.answer === "false" ? questionState.comments : null,
        files: questionState.answer === "true" ? questionState.files : [],
        keepFiles:
          questionState.answer === "true" && questionState.files.length === 0,
      };

      await handleUpdateAnswer(payload);

      setIsEditing(false);
      setOpenConfirm(false);
    } catch (error) {
      console.error("Erro ao atualizar resposta:", error);
      message.error(
        error.message || "Erro ao atualizar resposta. Tente novamente."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <S.QuestionItemContainer>
        <h4>{item.question_text || item.question}</h4>

        {item.question_type === "multiple_answers" ? (
          <Checkbox.Group
            options={(item.options || []).map((option) => ({
              label: option,
              value: option,
            }))}
            value={questionState.multipleAnswers}
            onChange={handleOnChangeMultipleAnswers}
            disabled={!isEditing && (!!answer?.id || finished)}
            style={{ display: "flex", flexDirection: "column", gap: "8px" }}
          />
        ) : item.question_type === "text" ? (
          <textarea
            id={`text-answer-question-${item.id}`}
            name={`text-answer-question-${item.id}`}
            placeholder="Digite sua resposta aqui..."
            rows={4}
            maxLength={500}
            value={questionState.textAnswer}
            onChange={handleOnChangeTextAnswer}
            disabled={!isEditing && (!!answer?.id || finished)}
            style={{
              width: "100%",
              padding: "8px",
              border: "1px solid #d9d9d9",
              borderRadius: "6px",
              resize: "vertical",
            }}
          />
        ) : (
          <Radio.Group
            id={`answer-question-${item.id}`}
            name={`answer-question-${item.id}`}
            onChange={handleOnChangeAnswer}
            value={questionState.answer}
            style={{ display: "flex", flexDirection: "row" }}
            disabled={!isEditing && (!!answer?.id || finished)}
          >
            <Radio value="true">SIM</Radio>
            <Radio value="false">NÃO</Radio>
          </Radio.Group>
        )}

        <div style={{ marginBottom: 16 }} />

        {questionState.answer === "true" &&
          item.question_type !== "multiple_answers" &&
          item.question_type !== "text" && (
            <>
              {(isEditing || !answer?.id) && (
                <input
                  name={`files-question-${item.id}`}
                  id={`files-question-${item.id}`}
                  type="file"
                  multiple={true}
                  onChange={handleOnChangeFiles}
                  accept="image/*,application/pdf"
                />
              )}
            </>
          )}

        {questionState.files.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            {questionState.files.map((i, index) => (
              <S.FileItemContainer key={index}>
                {finished || (!!answer?.id && !isEditing) ? (
                  <a
                    href={`https://plataforma-checkin-answers-files.s3.sa-east-1.amazonaws.com/${i.key}`}
                    download={i.filename}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {i.filename}
                  </a>
                ) : (
                  <h6>{i.name}</h6>
                )}
              </S.FileItemContainer>
            ))}
          </div>
        )}

        {questionState.answer === "false" &&
          item.question_type !== "multiple_answers" &&
          item.question_type !== "text" && (
            <S.CommentsContainer>
              <h5>Justificativa:</h5>
              {item.is_multiple_choice ? (
                <Radio.Group
                  onChange={(e) =>
                    setQuestionState({
                      ...questionState,
                      comments: e.target.value,
                    })
                  }
                  value={questionState.comments}
                  disabled={
                    (finished && !isEditing) || (!!answer?.id && !isEditing)
                  }
                >
                  {item.options.map((option, index) => (
                    <Radio key={index} value={option}>
                      {option}
                    </Radio>
                  ))}
                </Radio.Group>
              ) : (
                <textarea
                  id={`comments-question-${item.id}`}
                  name={`comments-question-${item.id}`}
                  placeholder=""
                  rows={2}
                  maxLength={100}
                  onBlur={handleOnChangeComments}
                  defaultValue={questionState.comments}
                  disabled={finished || (!!answer?.id && !isEditing)}
                />
              )}
            </S.CommentsContainer>
          )}

        <S.ActionContainer>
          {answer?.id ? (
            isEditing ? (
              <>
                <Button
                  type="primary"
                  onClick={() => setOpenConfirm(true)}
                  disabled={
                    loading ||
                    (item.question_type === "multiple_answers"
                      ? questionState.multipleAnswers.length === 0
                      : item.question_type === "text"
                      ? !questionState.textAnswer.trim()
                      : questionState.answer === null)
                  }
                >
                  {loading ? "Salvando..." : "Salvar Alterações"}
                </Button>
                <Button style={{ marginLeft: 8 }} onClick={handleCancelEdit}>
                  Cancelar
                </Button>
              </>
            ) : (
              <Button type="primary" onClick={handleEdit} disabled={loading}>
                Editar Resposta
              </Button>
            )
          ) : (
            <Button
              type="primary"
              onClick={() => setOpenConfirm(true)}
              disabled={
                loading ||
                finished ||
                (item.question_type === "multiple_answers"
                  ? questionState.multipleAnswers.length === 0
                  : item.question_type === "text"
                  ? !questionState.textAnswer.trim()
                  : questionState.answer === null)
              }
            >
              {loading ? "Salvando..." : "Salvar Resposta"}
            </Button>
          )}
        </S.ActionContainer>
      </S.QuestionItemContainer>

      <ConfirmationModal
        open={openConfirm}
        onCancel={() => setOpenConfirm(false)}
        onOk={answer?.id ? _handleUpdateAnswer : _handleSaveAnswer}
        okText="Confirmar"
      />
    </>
  );
};
